import {
  selectOrganizationId,
  useUploadImageMutation,
} from '@carboncopyco/data';
import { UploadFileIcon } from '@carboncopyco/icons';
import type { ImageVariable } from '@carboncopyco/types';
import {
  InputTextField,
  type InputTextFieldProps,
  Loading,
  Tooltip,
  notify,
} from '@carboncopyco/ui';
import { base64toFile } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useField } from 'formik';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { ImageDropzone, type ImageDropzoneProps } from '../ImageDropzone';
import { RuleIcon } from '../RuleIcon';
import { useFilesServiceContext } from '../utils';

export interface ImageVariableInputProps
  extends Pick<InputTextFieldProps, 'name'> {
  readOnly?: boolean;
  name: string;
}
const normalizeName = (oldName: string, newName: string) =>
  oldName.replace('.value', newName);

export const ImageVariableInput = ({
  readOnly,
  name,
}: ImageVariableInputProps) => {
  const { filesServiceDomain } = useFilesServiceContext();
  const organizationId = useSelector(selectOrganizationId);
  const [valueField, , valueHelpers] = useField<ImageVariable['value']>(name);
  const [nameField] = useField<ImageVariable['name']>(
    normalizeName(name, '.imageMetadata.name')
  );
  const [, , metadataHelpers] = useField<ImageVariable['imageMetadata']>(
    normalizeName(name, '.imageMetadata')
  );
  const inputRef = useRef(null);
  const [showUpload, setShowUpload] = useBoolean();
  const [loadingImage, setLoadingImage] = useBoolean();
  const [uploadImage] = useUploadImageMutation();

  const handleUploadImage: ImageDropzoneProps['onSuccess'] = async (image) => {
    const {
      url,
      metadata = {
        name: '',
        format: '',
        width: 0,
        height: 0,
      },
    } = image;
    try {
      const fileData = base64toFile(url);
      const { link_to_file: fileServiceLink } = await uploadImage({
        organizationId,
        fileData,
      }).unwrap();
      valueHelpers.setValue(fileServiceLink);
      metadataHelpers.setValue(metadata);
    } catch (e) {
      console.error(e);
      notify.error('Failed to upload image');
    }
    setLoadingImage.setFalse();
  };

  const getImageVariableUrl = (value: string) => {
    if (!value) {
      return '';
    }
    return value.includes(filesServiceDomain)
      ? value
      : `${filesServiceDomain}${value}`;
  };

  return (
    <>
      <input
        hidden
        value={valueField.value}
        name={valueField.name}
        readOnly
      />
      <div
        ref={inputRef}
        className={classNames({
          'cursor-pointer': !readOnly,
        })}
        onMouseEnter={setShowUpload.setTrue}
        onMouseLeave={setShowUpload.setFalse}
      >
        <ImageDropzone
          disabled={readOnly}
          onLoading={setLoadingImage.setTrue}
          onError={() => {
            setLoadingImage.setFalse();
            notify.error('Failed to upload image');
          }}
          onSuccess={handleUploadImage}
        >
          <InputTextField
            name={nameField.name}
            readOnly
            ellipsis
            noLabels
            noAddedHeight
            value={nameField.value}
            leftElement={
              <RuleIcon
                size="sm"
                backgroundImageUrl={getImageVariableUrl(valueField.value)}
              />
            }
            rightElement={
              showUpload && !loadingImage && !readOnly ? (
                <UploadButton />
              ) : loadingImage ? (
                <Loading color="text-ds-primary-300" />
              ) : undefined
            }
          />
        </ImageDropzone>
      </div>
      <Tooltip
        anchorRef={inputRef}
        placement="top-start"
        noArrow
      >
        <img
          className="max-h-[128px] max-w-[128px] rounded-md shadow-ds-lg"
          src={getImageVariableUrl(valueField.value)}
          alt="imageVariable"
        />
      </Tooltip>
    </>
  );
};

const UploadButton = () => {
  return (
    <span className="flex gap-1 items-center text-ds-primary-300 font-ds-medium text-ds-200">
      <UploadFileIcon size={16} />
      <span>Upload</span>
    </span>
  );
};
